import { useAbility } from '@casl/react';
import React, { useCallback, useEffect, useState } from 'react';

import {
    addAsyncResponseTransform,
    api,
    apiAddAsyncRequestTransform,
    publicApi,
} from '@barboxx/api';
import { UserTypes } from '@barboxx/types';
import type { Authentication } from '@barboxx/models/types';

import { AbilityContext, AuthContextProps, PersistedData } from '../context';

interface AuthToastError {
    title: string;
    description: string;
}

type PersistKeys = 'refreshToken' | 'email' | 'rememberMe';
type PersistData = {
    email?: string;
    rememberMe?: boolean;
    refreshToken?: string;
};

interface PersistOptions {
    get: (keys: PersistKeys[]) => Promise<PersistData>;
    clear: (keys: PersistKeys[]) => Promise<void>;
    set: (data: PersistData) => Promise<void>;
    storeRefreshToken?: boolean;
}

export interface AuthServiceConfigFunctions<
    T extends Authentication.AuthReturn = Authentication.AuthReturn,
> {
    // Optional functions
    onError?: (error: AuthToastError) => void;
    onLogin?: (authReturn: AuthToastError) => void;
    onSetUser?: (user: T['user']) => void;
    persistOptions?: PersistOptions;
    hasRefreshToken?: () => Promise<boolean>;
}

export interface AuthService<
    T extends Authentication.AuthReturn = Authentication.AuthReturn,
> extends AuthServiceConfigFunctions<T> {
    login: (
        data: Authentication.LoginRequest,
        allowedUserTypes?: UserTypes[]
    ) => Promise<T>;
    logout: () => Promise<undefined>;
    refreshExpiredToken: (
        query?: Authentication.RefreshTokenPayload
    ) => Promise<T>;
}

export interface AuthProviderProps {
    onLogOut?: () => Promise<void> | void;
}

export function createAuthProvider<
    T extends Authentication.AuthReturn = Authentication.AuthReturn,
>(
    AuthContext: React.Context<AuthContextProps<T>>,
    authService: AuthService<T>
) {
    return function AuthProvider({
        children,
        onLogOut,
    }: React.PropsWithChildren<AuthProviderProps>) {
        // swap this to Xstate
        const ability = useAbility(AbilityContext);

        const [initializing, setInitializing] = useState<boolean>(true);
        const [initialized, setInitialized] = useState<boolean>(false);
        const [auth, updateAuth] = useState<T>({} as T);
        const [persist, setPersist] = useState<PersistedData>({
            rememberMe: false,
            email: '',
        });

        const revokeAuthorizationPromiseRef =
            React.useRef<Promise<void> | null>(null);

        const initializationCompleted = React.useCallback(() => {
            setInitialized(true);
            setInitializing(false);
        }, []);

        const setPersistedData = React.useCallback(
            ({ rememberMe, email, refreshToken }: PersistData) => {
                if (authService.persistOptions) {
                    if (
                        authService.persistOptions.storeRefreshToken &&
                        refreshToken
                    ) {
                        authService.persistOptions?.set({ refreshToken });
                    }

                    switch (rememberMe) {
                        case true:
                            authService.persistOptions?.set({
                                email,
                                rememberMe,
                            });
                            setPersist({ email, rememberMe });
                            break;
                        case false:
                            authService.persistOptions.clear([
                                'email',
                                'rememberMe',
                            ]);
                            setPersist({ email: '', rememberMe: false });
                            break;
                    }
                }
            },
            []
        );

        const setAuth = useCallback(
            async (authResponse: T, rememberMe?: boolean) => {
                const { token, expiresAt, refreshToken, user } = authResponse;
                api.setTokenInfo({ token, expiresAt });
                updateAuth(prev => ({ ...prev, ...authResponse }));
                ability.update(user.rules);

                if (authService.onSetUser) {
                    authService.onSetUser(user);
                }

                setPersistedData({
                    email: user.email,
                    rememberMe,
                    refreshToken,
                });

                initializationCompleted();
            },
            [ability, initializationCompleted, setPersistedData]
        );

        const revokeAuthorization = useCallback(async () => {
            api.clearTokenInfo();
            if (authService.persistOptions?.storeRefreshToken) {
                await authService.persistOptions.clear(['refreshToken']);
            }

            ability.update([]);
            updateAuth({} as T);
            initializationCompleted();
        }, [ability, initializationCompleted]);

        const revokeAuthorizationOnce = useCallback(async () => {
            if (revokeAuthorizationPromiseRef.current) {
                return revokeAuthorizationPromiseRef.current;
            }

            revokeAuthorizationPromiseRef.current =
                revokeAuthorization().finally(() => {
                    revokeAuthorizationPromiseRef.current = null;
                });

            return revokeAuthorizationPromiseRef.current;
        }, [revokeAuthorization]);

        const logout = useCallback(
            () =>
                authService.logout().finally(async () => {
                    await revokeAuthorizationOnce();
                    await onLogOut?.();
                }),
            [onLogOut, revokeAuthorizationOnce]
        );

        const refreshExpiredToken = useCallback(
            () =>
                authService.refreshExpiredToken().then(result => {
                    // Success means data exists
                    setAuth(result);
                    return result;
                }),
            [setAuth]
        );

        useEffect(() => {
            publicApi.addAsyncResponseTransform(
                addAsyncResponseTransform({
                    // unauthorizedCallback: revokeAuthorizationOnce,
                    networkErrorCallback: error => {
                        authService?.onError &&
                            authService.onError({
                                description: error.message,
                                title: 'Public Api Error',
                            });
                    },
                })
            );

            api.addAsyncResponseTransform(
                addAsyncResponseTransform({
                    unauthorizedCallback: revokeAuthorizationOnce,
                    networkErrorCallback: error => {
                        authService?.onError &&
                            authService.onError({
                                description: error.message,
                                title: 'Api Error',
                            });
                    },
                })
            );
        }, [logout, revokeAuthorizationOnce]);

        useEffect(() => {
            if (auth.refreshToken) {
                api.addAsyncRequestTransform(
                    apiAddAsyncRequestTransform({
                        refreshExpiredToken,
                    })
                );
            }
        }, [auth.refreshToken, refreshExpiredToken]);

        useEffect(() => {
            const authorizationCheck = async () => {
                if (authService.persistOptions) {
                    setInitializing(true);

                    const { email, rememberMe } =
                        await authService.persistOptions.get([
                            'email',
                            'rememberMe',
                        ]);

                    if (rememberMe) {
                        setPersist({ rememberMe, email });
                    }

                    if (await authService.hasRefreshToken?.()) {
                        authService
                            .refreshExpiredToken()
                            .then(result => {
                                setAuth(result, rememberMe);
                                return;
                            })
                            .catch(e => {
                                revokeAuthorization();
                                return;
                            });
                    }
                }

                setTimeout(() => {
                    // Simulate a delay for initialization
                    initializationCompleted();
                }, 500);
            };

            if (!auth.token && !initialized) {
                authorizationCheck();
            }
        }, [
            ability,
            auth.token,
            initialized,
            initializationCompleted,
            logout,
            revokeAuthorization,
            setAuth,
        ]);

        const login = React.useCallback(
            async (data: Authentication.LoginRequest) => {
                const response = await authService.login(data);
                await setAuth(response, data.rememberMe);
                return response;
            },
            [setAuth]
        );

        const value = React.useMemo(
            () => ({
                auth,
                login,
                logout,
                refreshExpiredToken,
                initializing,
                persist,
            }),
            [auth, initializing, login, logout, persist, refreshExpiredToken]
        );

        return (
            <AuthContext.Provider value={value}>
                {children}
            </AuthContext.Provider>
        );
    };
}
